/**
 * GenerationProgress — shared 5-stage progress strip + cycling tips.
 *
 * Lifted from FeasibilityRerunPanel's inline running-state markup so
 * the wizard (Workflow Prompt 5) can mount the same strip during
 * generation. A user who fires a fresh report and a user who reruns
 * an existing one now see identical progress UX.
 *
 * Stages map onto the report-generation pipeline:
 *
 *   Collect  bootstrap-wait / top-up of provider data
 *   Compile  SED load + cross-validation
 *   Analyze  Stage 1 narrative generation
 *   Verify   citation audit + fact-check
 *   Review   iterative review loop + final sanity
 *
 * Stage detection is two-pass: pattern-match on the human-readable
 * `progressStage` string the store emits, with a numeric-threshold
 * fallback so the strip still moves when a future pipeline change
 * tweaks the prose. Both inputs come straight off the `siteRag`
 * slice — no extra plumbing needed at the call site.
 */
import React from 'react';
import { Loader2, RefreshCw, AlertTriangle, Clock3 } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { normalizeProgressPercent } from '../../lib/coerceNumber';
import { GenerationErrorSourceLink } from './GenerationErrorSourceLink';
import { generationCountdownPresentation } from '../../lib/reportGeneration/generationEta';
import {
  isSentimentEvidenceWaitStage,
  SENTIMENT_EVIDENCE_WAITING_NOTICE,
} from '../../lib/sentiment/sentimentEvidenceReadiness';

export type StageIndex = 0 | 1 | 2 | 3 | 4;

export { normalizeProgressPercent };

export interface GenerationTiming {
  startedAt?: string | null;
  estimatedDurationMs?: number | null;
}

/** Minute-granularity live ETA; the leading `~` makes its uncertainty explicit. */
export const GenerationCountdown: React.FC<GenerationTiming & {
  fallback?: React.ReactNode;
  testId?: string;
}> = ({ startedAt, estimatedDurationMs, fallback = 'Analysing', testId }) => {
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!startedAt || !estimatedDurationMs) return;
    // A mounted row can be reused for a replacement run. Refresh immediately
    // so it never displays the prior run's clock until the first interval tick.
    setNowMs(Date.now());
    const id = window.setInterval(() => setNowMs(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [estimatedDurationMs, startedAt]);
  const presentation = generationCountdownPresentation({ startedAt, estimatedDurationMs, nowMs });
  if (!presentation) return <>{fallback}</>;
  return (
    <span
      aria-label={presentation.ariaLabel}
      role="timer"
      data-overdue={presentation.overdue ? 'true' : 'false'}
      data-testid={testId}
      className="tabular-nums"
    >
      {presentation.label}
    </span>
  );
};

/** FALLBACK staleness window — used ONLY when no server heartbeat is available
 *  (legacy/sync runs with no job doc). Watches the (stage, pct) pair: if it has
 *  not changed for this long while a run is in flight, the strip flips to
 *  "stalled". This is a BLUNT signal — a long phase with a flat bar trips it
 *  even though the job is alive — which is exactly why the heartbeat path below
 *  is preferred whenever a job heartbeat is present. */
const PROGRESS_STALL_MS = 180_000; // 3 minutes with no % movement (fallback only)

/** PREFERRED staleness window — keyed off the SERVER HEARTBEAT (`heartbeatAt`),
 *  which the runner refreshes on every write plus a 30s coarse ticker, so it is
 *  never older than ~30s while the container is genuinely alive. We re-arm the
 *  stall clock every time that beat CHANGES (clock-skew-proof: we measure the
 *  gap in client time since the beat last advanced, never compare the two
 *  clocks). 2 minutes ⇒ ~4 missed 30s beats — well past any normal Firestore
 *  listener blip or slow patch, so the banner only appears when the process is
 *  genuinely dead/hung. This is the "only show it when it is REALLY stalled"
 *  guarantee, mirroring the SED banner's heartbeat-based isHeartbeatStale. */
export const HEARTBEAT_STALL_MS = 120_000; // 2 minutes with no fresh server beat

/**
 * Returns true once `key` has been unchanged for `stallMs` while `active`.
 * Re-arms whenever `key` changes (progress moved) or `active` toggles. Ticks
 * on a bounded interval so the staleness flips within ~one tick of crossing
 * the threshold without spinning the event loop on an idle/complete card.
 */
export function useProgressStall(key: string, active: boolean, stallMs: number = PROGRESS_STALL_MS): boolean {
  const changedAtRef = React.useRef<number>(Date.now());
  const [stalled, setStalled] = React.useState(false);
  // Reset the clock whenever the progress key moves or the run (de)activates.
  React.useEffect(() => {
    changedAtRef.current = Date.now();
    setStalled(false);
  }, [key, active]);
  React.useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => {
      if (Date.now() - changedAtRef.current >= stallMs) setStalled(true);
    }, 20_000);
    return () => window.clearInterval(id);
  }, [key, active, stallMs]);
  return stalled;
}

const STAGE_LABELS: readonly [string, string, string, string, string] = [
  'Collect', 'Compile', 'Analyze', 'Verify', 'Review',
];

/** Threshold map used as a numeric-only fallback when the stage
 *  string doesn't match any known marker. Mirrors the legacy values
 *  baked into FeasibilityRerunPanel — keep in sync if either drifts. */
const PROGRESS_THRESHOLDS: readonly [number, number, number, number, number] = [10, 25, 35, 70, 80];

/**
 * Resolve the active stage from the store's `progressStage` string +
 * `progress` number. Pattern-matches first (more robust to threshold
 * tweaks) then falls back to the legacy numeric thresholds.
 */
export function stageFromProgressString(stageText: string, progress: unknown): StageIndex {
  const pct = normalizeProgressPercent(progress);
  const s = (stageText ?? '').toLowerCase();
  // Order matters — check the latest-stage patterns first so a
  // string like "Phase 5b sanity" doesn't match the Phase 1 branch.
  if (/phase 5b|phase 5\b|review|sanity/.test(s)) return 4;
  if (/phase 4|verify|citation/.test(s)) return 3;
  if (/phase 3|analy[sz]|stage 1|narrative/.test(s)) return 2;
  if (/phase 2|compil|building site evidence|sed load/.test(s)) return 1;
  if (/phase 1|collect|querying|gather|bootstrap|top.?up/.test(s)) return 0;

  // Fallback — derive from numeric progress.
  if (pct >= PROGRESS_THRESHOLDS[4]) return 4;
  if (pct >= PROGRESS_THRESHOLDS[3]) return 3;
  if (pct >= PROGRESS_THRESHOLDS[2]) return 2;
  if (pct >= PROGRESS_THRESHOLDS[1]) return 1;
  return 0;
}

/** Tips per stage — cycle while the stage is active. Sourced from
 *  the legacy phase-keyed copy so the messaging the user sees during
 *  rerun stays the same after the refactor. */
const STAGE_TIPS: readonly string[][] = [
  [
    'Querying 15 government & industry APIs…',
    'Open-Meteo, USGS, FEMA, HIFLD, NRC, WRI, PeeringDB…',
  ],
  [
    'Cross-validating data from multiple sources',
    'Building Site Evidence Document with uploaded documents',
  ],
  [
    'Datacrate analyzing full site evidence + documents',
    'Long reports can take about an hour; timing varies with evidence and review',
  ],
  [
    'Verifying every claim against evidence + documents',
    'Computing fact-check score',
  ],
  [
    'Datacrate reviewing report quality (score target: 95+)',
    'May iterate up to 4 times — please be patient',
  ],
];

/** Resolve cycling tips for a given stage string + progress pair. */
export function tipsForStage(stageText: string, progress: number): readonly string[] {
  const activeStage = stageFromProgressString(stageText, progress);
  return STAGE_TIPS[activeStage] ?? ['Processing…'];
}

export interface GenerationProgressStripProps {
  /** Pass `siteRag.progressStage` from the store. */
  progressStage: string;
  /** Pass `siteRag.progress` from the store (0–100). Coerced when non-numeric. */
  progress: unknown;
  /**
   * ISO of the server job's last liveness beat. It lives in the store's
   * `generationHeartbeats` slice (keyed by run id — `useGenerationHeartbeat`,
   * or `InflightRunProgressStrip` from a list render site), not on the run. When
   * provided, stall detection keys off THIS advancing — never older than ~30s
   * while alive — so a long phase with a flat bar is NOT flagged stalled; only a
   * genuinely dead/hung process trips it. Omit (sync/legacy runs) to fall back
   * to the blunt (stage, pct)-change detector.
   */
  heartbeatAt?: string | null;
  /** Stable enqueue-time anchor + server-stamped approximate duration. */
  startedAt?: string | null;
  estimatedDurationMs?: number | null;
  /** When true, render the cycling tip row below the chip strip. */
  showTips?: boolean;
  /** Appended to strip test ids — e.g. `-run-abc` for concurrent cards. */
  testIdSuffix?: string;
  /** Tighter spacing for sidebar cards. */
  compact?: boolean;
}

export const GenerationProgressStrip: React.FC<GenerationProgressStripProps> = ({
  progressStage,
  progress,
  heartbeatAt,
  startedAt,
  estimatedDurationMs,
  showTips = false,
  testIdSuffix = '',
  compact = false,
}) => {
  const pct = normalizeProgressPercent(progress);
  const activeStage = stageFromProgressString(progressStage, pct);
  const tips = STAGE_TIPS[activeStage] ?? ['Processing…'];
  // A Public Sentiment run held until a site-evidence build finishes has not
  // been dispatched yet: no countdown, no stall alarm, no active stage — a
  // waiting notice instead (the host card keeps its Cancel control).
  const waitingForEvidence = isSentimentEvidenceWaitStage(progressStage);
  // NEVER-STALE — only flag "stalled" when the run is REALLY dead, not merely
  // sitting in a long phase. `active` is false once we reach 100% so a finished
  // run never false-alarms.
  //
  // PREFERRED: key off the SERVER HEARTBEAT advancing. `heartbeatAt` changes
  // every ≤30s while the job is alive, so re-arming the stall clock on each
  // change means a flat % during a long ANALYZE/Review phase does NOT trip it —
  // only a genuinely dead/hung process (beat stops) does, after HEARTBEAT_STALL_MS.
  // FALLBACK (no heartbeat — sync/legacy runs): the blunt (stage,pct) detector.
  const hasHeartbeat = typeof heartbeatAt === 'string' && heartbeatAt.length > 0;
  const active = pct < 100 && !waitingForEvidence;
  const stallKey = hasHeartbeat ? `hb:${heartbeatAt}` : `${progressStage}::${pct}`;
  const stallMs = hasHeartbeat ? HEARTBEAT_STALL_MS : PROGRESS_STALL_MS;
  const stalled = useProgressStall(stallKey, active, stallMs);

  return (
    <div className={compact ? 'space-y-1.5' : 'space-y-2'} data-stalled={stalled ? 'true' : 'false'}>
      <div className="flex justify-between type-body uppercase tracking-widest text-text-secondary">
        <span className="flex items-center gap-1.5">
          {waitingForEvidence
            ? <Clock3 className="w-3 h-3" aria-hidden />
            : stalled
            ? <AlertTriangle className="w-3 h-3 text-warning" data-testid={`generation-progress-stalled-icon${testIdSuffix}`} />
            : <Loader2 className="w-3 h-3 animate-spin" />}
          {waitingForEvidence
            ? 'Waiting for site evidence'
            : stalled
            ? 'Run stalled — no progress'
            : (
                <GenerationCountdown
                  startedAt={startedAt}
                  estimatedDurationMs={estimatedDurationMs}
                  fallback={progressStage || 'Initializing…'}
                  testId={`generation-countdown${testIdSuffix}`}
                />
              )}
        </span>
        <span data-testid={`generation-progress-pct${testIdSuffix}`}>{pct}%</span>
      </div>
      <Progress value={pct} className={`h-1.5 rounded-full bg-state-hover ${stalled ? 'opacity-50' : ''}`} />

      {/* NEVER-STALE banner — replaces the frozen "still working" illusion with
          an honest stalled signal + the action the user needs. The host card's
          Cancel/retry control stays visible alongside this. */}
      {stalled && (
        <div
          className="mt-1 px-2 py-1.5 rounded-sm border border-warning/40 bg-warning/10 type-meta text-warning leading-relaxed"
          role="alert"
          data-testid={`generation-progress-stalled${testIdSuffix}`}
        >
          This run hasn’t made progress in a few minutes — it may have stalled.
          Cancel it and try again.
        </div>
      )}

      {waitingForEvidence && (
        <div
          className="mt-1 px-2 py-1.5 rounded-sm border border-primary/40 bg-primary/5 type-meta text-text-secondary leading-relaxed"
          role="status"
          data-testid={`generation-progress-evidence-wait${testIdSuffix}`}
        >
          {SENTIMENT_EVIDENCE_WAITING_NOTICE}
        </div>
      )}

      {/* 5-stage chip strip. The active chip pulses; past chips
          keep the active-tone background; future chips stay muted. */}
      <div className="flex gap-1 mt-1" data-testid={`generation-progress-strip${testIdSuffix}`}>
        {STAGE_LABELS.map((label, i) => {
          const isActive = !waitingForEvidence && i === activeStage;
          const isPast = !waitingForEvidence && i < activeStage;
          return (
            <div
              key={label}
              data-testid={`generation-progress-chip-${label.toLowerCase()}${testIdSuffix}`}
              data-active={isActive ? 'true' : 'false'}
              data-past={isPast ? 'true' : 'false'}
              className={`flex-1 text-center py-0.5 type-meta font-bold uppercase tracking-widest rounded-sm transition-colors ${
                isActive
                  ? 'bg-primary text-text-primary animate-pulse'
                  : isPast
                    ? 'bg-state-active text-primary'
                    : 'bg-state-hover text-text-muted'
              }`}
            >
              {label}
            </div>
          );
        })}
      </div>

      {/* No "collecting provider data" tips for a run that has not started. */}
      {showTips && !waitingForEvidence && (
        <div className="pt-1.5 border-t border-border-subtle">
          <CyclingTip tips={tips} testIdSuffix={testIdSuffix} />
        </div>
      )}
    </div>
  );
};

interface Props {
  /** Pass `siteRag.progressStage` from the store. */
  progressStage: string;
  /** Pass `siteRag.progress` from the store (0–100). */
  progress: number;
  /** ISO of the server job's last liveness beat (`currentRun.heartbeatAt`) so
   *  stall detection keys off the heartbeat advancing, not the % moving. */
  heartbeatAt?: string | null;
  /** Stable enqueue-time anchor for the approximate countdown. */
  startedAt?: string | null;
  /** Server-stamped approximate total generation duration. */
  estimatedDurationMs?: number | null;
  /** Closes the host modal but lets generation continue in the
   *  background. The host (rerun panel / wizard) is responsible for
   *  the actual close + any toast. */
  onContinueInBackground: () => void;
  /** Optional cancel hook. Not wired today — generation is
   *  uninterruptible — so callers pass undefined to hide the button. */
  onCancel?: () => void;
  /** Title shown above the strip — e.g. "Generating v2" for a rerun
   *  or "Generating Feasibility report" for a new run. */
  title: string;
  /** Optional inline error rendered below the strip when generation
   *  fails. The host owns the recovery CTA; we just surface the
   *  message. */
  error?: string | null;
}

export const GenerationProgress: React.FC<Props> = ({
  progressStage,
  progress,
  heartbeatAt,
  startedAt,
  estimatedDurationMs,
  onContinueInBackground,
  onCancel,
  title,
  error,
}) => {
  // A Public Sentiment run waiting for a site-evidence build has not been
  // dispatched: no spinner, and no promise that a report is being saved.
  const waitingForEvidence = isSentimentEvidenceWaitStage(progressStage);
  return (
    <div className="p-6 space-y-4" data-testid="generation-progress">
      <div className="text-center space-y-2 pb-4">
        <div className="w-16 h-16 mx-auto bg-state-hover rounded-full flex items-center justify-center">
          {waitingForEvidence
            ? <Clock3 className="w-8 h-8 text-text-primary" aria-hidden data-testid="generation-progress-waiting-icon" />
            : <RefreshCw className="w-8 h-8 text-text-primary animate-spin" />}
        </div>
        <div className="text-xs font-bold uppercase tracking-widest text-text-primary">
          {title}
        </div>
      </div>

      <div className="space-y-2 p-4 border border-border bg-state-hover">
        <GenerationProgressStrip
          progressStage={progressStage}
          progress={progress}
          heartbeatAt={heartbeatAt}
          startedAt={startedAt}
          estimatedDurationMs={estimatedDurationMs}
          showTips
        />
      </div>

      {error && (
        <div
          className="p-3 border border-danger/40 bg-danger/15 space-y-1"
          data-testid="generation-progress-error"
          role="alert"
        >
          <div className="type-body font-bold text-danger uppercase tracking-widest">
            Generation failed
          </div>
          <p className="type-meta text-danger leading-relaxed">{error}</p>
          <GenerationErrorSourceLink error={error} />
        </div>
      )}

      <div className="text-center space-y-1">
        <Button
          variant="outline"
          size="sm"
          className="h-8 type-meta uppercase tracking-widest text-text-secondary"
          onClick={onContinueInBackground}
          data-testid="generation-progress-continue-bg"
        >
          Continue in Background
        </Button>
        <p className="type-meta text-text-secondary">
          {waitingForEvidence
            ? 'Close this panel and keep working. The analysis has not started yet; keep this page open so it can start automatically.'
            : 'Close this panel and continue working. Report will save automatically.'}
        </p>
        {onCancel && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 type-meta uppercase tracking-widest text-text-secondary"
            onClick={onCancel}
            data-testid="generation-progress-cancel"
          >
            Cancel generation
          </Button>
        )}
      </div>
    </div>
  );
};

/** Cycling tip — fades through the supplied list every 4s. */
const CyclingTip: React.FC<{ tips: readonly string[]; testIdSuffix?: string }> = ({
  tips,
  testIdSuffix = '',
}) => {
  const [idx, setIdx] = React.useState(0);
  const [fade, setFade] = React.useState(true);
  React.useEffect(() => {
    if (tips.length <= 1) return;
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIdx(i => (i + 1) % tips.length);
        setFade(true);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, [tips]);
  return (
    <div
      className={`type-meta text-text-muted uppercase tracking-widest leading-relaxed transition-opacity duration-300 ${fade ? 'opacity-100' : 'opacity-0'}`}
      data-testid={`generation-progress-tip${testIdSuffix}`}
    >
      {tips[idx] ?? ''}
    </div>
  );
};
